import { ApiResponse } from 'apisauce';
import { CatalogCreationAccessFieldName } from '../../../modules/catalogs/creation/catalogCreationAccess.types';
import { PaginatedResponse } from '../../../types/request.types';
import { cancelTokenFromSignal } from '../../../utils/axios.utils';
import { formatDateToWithoutTimezone } from '../../../utils/dates';
import { restApi } from '../../api';
import {
  BuildCatalogAccessTargetPayloadParams,
  CatalogAccessEndpointConfig,
  CatalogAccessEntitiesDatesParams,
  CatalogAccessEntityDTO,
  FindCatalogAccessEntitiesParams,
  RemoveCatalogAccessEntitiesParams,
} from './types/catalogAccess.types';

export const CATALOG_ACCESS_ENDPOINT_BY_FIELD: Record<
  CatalogCreationAccessFieldName,
  CatalogAccessEndpointConfig
> = {
  classificationPermissions: {
    path: 'classification-permissions',
    idsField: 'customerClassificationIds',
    nameField: 'name',
  },
  customers: {
    path: 'customers',
    idsField: 'customerIds',
    nameField: 'commercialName',
  },
  salesRepresentatives: {
    path: 'sales-representatives',
    idsField: 'salesRepresentativeIds',
    nameField: 'name',
  },
};

export const findCatalogAccessEntities = ({
  catalogId,
  field,
  params,
  signal,
}: FindCatalogAccessEntitiesParams): Promise<
  ApiResponse<PaginatedResponse<CatalogAccessEntityDTO>>
> =>
  restApi.get<PaginatedResponse<CatalogAccessEntityDTO>>(
    `/catalogs/${catalogId}/${CATALOG_ACCESS_ENDPOINT_BY_FIELD[field].path}`,
    params,
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

export const addCatalogAccessEntities = ({
  catalogId,
  field,
  entityIds,
  startDate,
  endDate,
}: CatalogAccessEntitiesDatesParams): Promise<ApiResponse<void>> => {
  const { path, idsField } = CATALOG_ACCESS_ENDPOINT_BY_FIELD[field];

  return restApi.post<void>(`/catalogs/${catalogId}/${path}`, {
    [idsField]: entityIds,
    startDate: formatDateToWithoutTimezone(startDate),
    endDate: formatDateToWithoutTimezone(endDate),
  });
};

const buildCatalogAccessTargetPayload = ({
  idsField,
  entityIds,
  isApplyingToAll,
}: BuildCatalogAccessTargetPayloadParams) =>
  isApplyingToAll ? { applyToAll: true } : { [idsField]: entityIds };

export const updateCatalogAccessEntitiesDates = ({
  catalogId,
  field,
  entityIds,
  isApplyingToAll,
  startDate,
  endDate,
}: CatalogAccessEntitiesDatesParams): Promise<ApiResponse<void>> => {
  const { path, idsField } = CATALOG_ACCESS_ENDPOINT_BY_FIELD[field];

  return restApi.patch<void>(`/catalogs/${catalogId}/${path}/dates`, {
    ...buildCatalogAccessTargetPayload({ idsField, entityIds, isApplyingToAll }),
    startDate: formatDateToWithoutTimezone(startDate),
    endDate: formatDateToWithoutTimezone(endDate),
  });
};

export const removeCatalogAccessEntities = ({
  catalogId,
  field,
  entityIds,
  isApplyingToAll,
}: RemoveCatalogAccessEntitiesParams): Promise<ApiResponse<void>> => {
  const { path, idsField } = CATALOG_ACCESS_ENDPOINT_BY_FIELD[field];

  return restApi.delete<void>(`/catalogs/${catalogId}/${path}`, undefined, {
    data: buildCatalogAccessTargetPayload({ idsField, entityIds, isApplyingToAll }),
  });
};
